import { z } from 'zod';

export enum Rating {
  Neutral = 'neutral',
  Positive = 'positive',
  Negative = 'negative',
}

export enum FeedbackCategory {
  IrrelevantInformation = 'irrelevantInformation',
  MissingInformation = 'missingInformation',
  InsufficientDetail = 'insufficientDetail',
  IncorrectInformation = 'incorrectInformation',
  Other = 'other',
}

export enum ChatRole {
  User = 'user',
  Administrator = 'administrator',
  Assistant = 'assistant',
  Status = 'status',
}

export enum Role {
  SuperAdmin = 'superAdmin',
  Admin = 'admin',
}

export enum RoleWOSuperAdmin {
  Admin = 'admin',
}

export enum Agent {
  VoicebotRag = 'voicebotRag',
  ConversationalRagV3 = 'conversationalRagV3',
  VoiceChat = 'voiceChat',
  ShopAssistant = 'shopAssistant',
  Conversational = 'conversational',
}

/**
 * OpenAI: https://platform.openai.com/docs/models/gpt-4o
 * Anthropic: https://docs.anthropic.com/en/docs/about-claude/models#model-comparison-table
 * openrouter.ai: https://openrouter.ai/
 */
export enum ChatModel {
  // Anthropic models
  Claude3Opus = 'claude-3-opus-20240229',
  Claude35Sonnet20241022 = 'claude-3-5-sonnet-20241022',
  Claude35Sonnet20240620 = 'claude-3-5-sonnet-20240620',
  Claude35Sonnet = 'claude-3-5-sonnet-latest',
  Claude3Haiku = 'claude-3-haiku-20240307',

  // OpenAI models
  Gpt41 = 'gpt-4.1',
  Gpt41Mini = 'gpt-4.1-mini',
  Gpt41Nano = 'gpt-4.1-nano',
  Gpt4o_2024_08_06 = 'gpt-4o-2024-08-06',
  Gpt4o_2024_11_20 = 'gpt-4o-2024-11-20',
  Gpt4o = 'gpt-4o',
  Gpt4oMini = 'gpt-4o-mini',
  GptO1 = 'gpt-o1',
  GptO1_Mini = 'gpt-o1-mini',
  GptO3Mini = 'o3-mini-2025-01-31',

  // OpenRouter.ai models
  DeepSeekV3 = 'deepseek/deepseek-chat',
  MythomaxL213b = 'gryphe/mythomax-l2-13b',
  Mistral8B = 'mistralai/ministral-8b',
  Mistral3B = 'mistralai/ministral-3b',
  MetaLlama3170bInstruct = 'meta-llama/llama-3.1-70b-instruct',
  Gemini2Flash = 'google/gemini-2.0-flash-001',
  Gemini2FlashLite = 'google/gemini-2.0-flash-lite-001',
}

/**
 * Embedding models
 */
export enum EmbeddingModel {
  // OpenAI models
  TextEmbeddingAda002 = 'text-embedding-ada-002',
  TextEmbedding3Small = 'text-embedding-3-small',
  TextEmbedding3Large = 'text-embedding-3-large',

  // JinaAI models
  JinaEmbeddingsV3 = 'jina-embeddings-v3',
}

export enum ContentType {
  Website = 'website',
  Document = 'document',
  ExternalDocument = 'externalDocument',
}

export enum ToolType {
  Text = 'text',
  Tool = 'tool',
}

export enum TonePreset {
  Friendly = 'friendly',
  Casual = 'casual',
  Balanced = 'balanced',
  Professional = 'professional',
  Formal = 'formal',
}

export enum ResponseLengthPreset {
  VeryShort = 'very_short',
  Short = 'short',
  Medium = 'medium',
  Long = 'long',
  VeryLong = 'very_long',
}

export enum Language {
  Czech = 'cs',
  English = 'en',
}

export enum DocumentStatus {
  ParsingError = 'parsingError',
  EmbeddingError = 'embeddingError',
  Uploading = 'uploading',
  Ready = 'ready',
  Parsing = 'parsing',
  ParsingAsync = 'parsingAsync', // Represents a job that is scheduled but not yet completed
  Parsed = 'parsed',
  Embedding = 'embedding',
  Done = 'done',
}

export enum JobProvider {
  LlamaParse = 'llamaparse',
  Firecrawl = 'firecrawl',
  Mistral = 'mistral',
}

/**
 * Categories of usage records.
 */
// How about more categories? (Voice/Chat instead of agent)
export enum RecordCategory {
  Data = 'data',
  Agent = 'agent',
  Other = 'other',
  RAG = 'rag',
}

/**
 * Types of usage records. This identifies the actual operation that was performed.
 * Llm for LLM calls, Embedding for embedding calls, Parsing for parsing calls, etc.
 */
export enum RecordType {
  // All calls to the LLMs (agent nodes, chat calls, etc.)
  Llm = 'llm',

  // All calls to the TTS models
  TTS = 'tts',

  // All calls to the STT models
  STT = 'stt',

  // All calls to the embedding models
  Embedding = 'embedding',

  // All calls to the parsing models/services/tools
  Parsing = 'parsing',

  // All calls to the parsing models/services/tools
  Scraping = 'scraping',

  // All calls to the reranking models
  Rerank = 'rerank',

  // All other types
  Other = 'other',
}

/**
 * Types of units that can be used to track usage.
 */
export enum RecordUnit {
  Tokens = 'tokens',
  Characters = 'characters',
  Seconds = 'seconds',
  Credits = 'credits',
}

/**
 * Providers of the models/services/tools.
 */
export enum Provider {
  OpenAI = 'openai',
  OpenRouter = 'openrouter',
  Cohere = 'cohere',
  Anthropic = 'anthropic',
  Firecrawl = 'firecrawl',
  LlamaParse = 'llamaparse',
  JinaAI = 'jinaai',
  ElevenLabs = 'elevenlabs',
  Gladia = 'gladia',
  Deepgram = 'deepgram',
  Twilio = 'twilio',
  Mistral = 'mistral',
}

/**
 * Subset of providers that are STT providers.
 */
export enum STTProvider {
  Gladia = Provider.Gladia,
  Deepgram = Provider.Deepgram,
}

/**
 * Subset of providers that are TTS providers.
 */
export enum TTSProvider {
  ElevenLabs = Provider.ElevenLabs,
}

/**
 * Modes of the thread. Either user talks to human or AI.
 */
export enum ThreadMode {
  Human = 'human',
  AI = 'ai',
}

/**
 * Categories of the thread. Either voice or chat.
 */
export enum ThreadCategory {
  Voice = 'voice',
  Chat = 'chat',
}

/**
 * Types of agents.
 */
export enum AgentType {
  Chat = 'chat',
  Voice = 'voice',
}

/**
 * Names of the entities that can be associated with usage records.
 */
export enum EntityType {
  Document = 'document',
  ChatMessage = 'chatMessage',
  Thread = 'thread',
  Chat = 'chat',
  Feed = 'feed',
}

export enum ChatPlacementPosition {
  Left = 'left',
  Right = 'right',
}

export enum Gender {
  None = 'none',
  Male = 'male',
  Female = 'female',
}

export enum ThemePreset {
  Kanbu = 'kanbu',
  Light = 'light',
  Dark = 'dark',
}

export enum SourcesFormat {
  Simple = 'simple',
  Academic = 'academical',
}

export enum SubscriptionTier {
  Free = 'Free',
  Basic = 'Basic',
  Standard = 'Standard',
  Premium = 'Premium',
  Enterprise = 'Enterprise',
}

/**
 * Subscription Statuses aligned with Stripe.
 * See: https://stripe.com/docs/api/subscriptions/object#subscription_object-status
 */
export enum SubscriptionStatus {
  Active = 'active',
  Canceled = 'canceled',
  Incomplete = 'incomplete',
  IncompleteExpired = 'incomplete_expired',
  PastDue = 'past_due',
  Trialing = 'trialing',
  Unpaid = 'unpaid',
  // 'paused' is another potential Stripe status, but often handled application-side.
}

export enum BillingInterval {
  Month = 'month',
  Year = 'year',
}

export const subscriptionStatusEnum = z.nativeEnum(SubscriptionStatus);
export const subscriptionTierEnum = z.nativeEnum(SubscriptionTier);
export const billingIntervalEnum = z.nativeEnum(BillingInterval);

export enum CallLogEvent {
  CallStarted = 'call_started',
  CallEnded = 'call_ended',
  STT = 'stt',
  AI = 'ai',
  TTS = 'tts',
  Log = 'log',
  Error = 'error',
  Usage = 'usage',
}

export enum ElevenLabsVoices {
  Adam = 'uYFJyGaibp4N2VwYQshk',
  Jan = 'KIDKfqJyZ6ASuyzsKfh5',
  Ondra = 'nCmK9bolkZa2wzrAjnEe',
  Anet = 'SZXidiHhq5QYe3jRboSZ',
}

export enum OmnichannelPostbackAction {
  AcceptTakeover = 'accept_takeover',
  DeclineTakeover = 'decline_takeover',
}

export * from './enums/feedEnums';

export enum Currency {
  USD = 'usd',
  CZK = 'czk',
}

export enum ChargeType {
  BalanceTopUp = 'balance_top_up',
  Subscription = 'subscription',
  AutoRenewal = 'auto_renewal',
}
